import { Component, OnInit } from '@angular/core';
import {
  TenantListTableComponent,
  TenantViewModel,
} from './tenant-list-table/tenant-list-table.component';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import {
  TenantEditDialogComponent,
  TenantEditorDialogViewModel,
} from './tenant-edit-dialog/tenant-edit-dialog.component';
import { TenantDetailDailogComponent } from './tenant-detail-dailog/tenant-detail-dailog.component';
import { TenantDetailDialogViewModel } from './tenant-detail-dailog/tenant-detail-dailog.model';
import { TenantAddRequest, TenantUpdateRequest } from '@entities/tenant';
import { TenantService } from '@services/api/tenant.service';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';

@Component({
  selector: 'app-tenant-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonToggleModule,
    MatCardModule,
    TenantListTableComponent,
  ],
  templateUrl: './tenant-list.component.html',
  styleUrl: './tenant-list.component.scss',
})
export class TenantListComponent implements OnInit {
  /** テナント一覧 */
  public tenantList: TenantViewModel[] = [];

  constructor(
    private tenantService: TenantService,
    private dialog: MatDialog,
  ) {}

  public ngOnInit(): void {
    // テナント一覧を取得する
    this._initTenantList();
  }

  /**
   * テナント追加/編集ダイアログを表示する
   * @param tenant テナント
   */
  public async openTenantEditDialog(tenant?: TenantViewModel) {
    const options: TenantEditorDialogViewModel = {
      updating: false,
      tenantList: this.tenantList,
    };

    if (tenant) {
      options.updating = true;
      options.target = {
        tenantId: tenant.tenantId,
        tenantName: tenant.tenantName,
        companyName: tenant.companyName,
        description: tenant.description,
        companyLogoPath: tenant.companyLogoPath,
      };
    }

    const editedTenant: TenantViewModel = await firstValueFrom(
      this.dialog
        .open(TenantEditDialogComponent, {
          width: '700px',
          data: options,
        })
        .afterClosed(),
    );

    if (!editedTenant) {
      return;
    }

    if (options.updating) {
      // テナント更新処理
      const updatedTenantRequest: TenantUpdateRequest = {
        tenantId: editedTenant.tenantId,
        tenantName: editedTenant.tenantName,
        companyName: editedTenant.companyName,
        description: editedTenant.description,
        companyLogoPath: editedTenant.companyLogoPath,
      };
      this.tenantService.update(updatedTenantRequest).subscribe({
        next: () => {
          // テナント一覧を再取得する
          this._initTenantList();
        },
        error: (errorResponse) => {
          let alertDialogOptions: AlertDialogOption;
          if (
            errorResponse.response.error.detail.type ==
            'AlreadyExistsWebException'
          ) {
            alertDialogOptions = {
              type: 'error',
              message: `テナント名が重複しているため、更新に失敗しました。\n別の名前でもう一度お試しください。`,
            };
          } else if (
            errorResponse.response.status == 400 &&
            errorResponse.response.error.detail.type == 'NotFoundWebException'
          ) {
            alertDialogOptions = {
              type: 'error',
              message:
                '他のユーザーによってデータが変更されたため、更新に失敗しました。最新のデータを取得して再度試してください。',
            };
          } else {
            alertDialogOptions = {
              type: 'error',
              message: `テナント更新に失敗しました。\nしばらくしてからもう一度お試しください。`,
            };
          }
          this.dialog.open(AlertDialogComponent, {
            width: '600px',
            data: alertDialogOptions,
          });
        },
      });
    } else {
      // テナント追加処理
      const addedTenantRequest: TenantAddRequest = {
        tenantName: editedTenant.tenantName,
        companyName: editedTenant.companyName,
        description: editedTenant.description,
        companyLogoPath: editedTenant.companyLogoPath,
      };
      this.tenantService.add(addedTenantRequest).subscribe({
        next: () => {
          // テナント一覧を再取得する
          this._initTenantList();
        },
        error: (errorResponse) => {
          let alertDialogOptions: AlertDialogOption;

          if (
            errorResponse.response.error.detail.type ==
            'AlreadyExistsWebException'
          ) {
            alertDialogOptions = {
              type: 'error',
              message: `テナント名が重複しているため、追加に失敗しました。\n別の名前でもう一度お試しください。`,
            };
          } else {
            alertDialogOptions = {
              type: 'error',
              message: `テナント追加に失敗しました。\nしばらくしてからもう一度お試しください。`,
            };
          }
          this.dialog.open(AlertDialogComponent, {
            width: '600px',
            data: alertDialogOptions,
          });
        },
      });
    }
  }

  /**
   * テナント詳細表示ダイアログを表示する
   * @param tenantId テナントId
   */
  public async openTenantDetailDialog(tenantId: string) {
    // テナントIdをもとにバックエンドからテナント情報を取得し、ダイアログに渡す値としてセット
    this.tenantService.find(tenantId).subscribe({
      next: (tenantDetail) => {
        const options: TenantDetailDialogViewModel = {
          tenantId: tenantDetail.tenantId,
          tenantName: tenantDetail.tenantName,
          description: tenantDetail.description,
          companyName: tenantDetail.companyName,
          companyLogoPath: tenantDetail.companyLogoPath,
          adminCount: tenantDetail.adminCount,
          userCount: tenantDetail.userCount,
          deviceCounts: tenantDetail.deviceCounts,
          assetTypeCount: tenantDetail.assetTypeCount,
          shipmentCount: tenantDetail.shipmentCount,
          shipmentLocationCount: tenantDetail.shipmentLocationCount,
          shipmentRouteCount: tenantDetail.shipmentRouteCount,
        };
        // ダイアログを開く
        firstValueFrom(
          this.dialog
            .open(TenantDetailDailogComponent, {
              width: '700px',
              data: options,
            })
            .afterClosed(),
        );
      },
    });
  }

  /**
   * テナントを削除する
   * @param tenantId テナントId
   */
  public async deleteTenant(tenantId: string) {
    const options: AlertDialogOption = {
      type: 'error',
      title: 'Confirm',
      message: `選択したテナントを削除してよろしいですか？`,
      operation: '削除',
    };

    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      return;
    }

    this.tenantService.delete(tenantId).subscribe({
      next: () => {
        // テナント一覧を再取得する
        this._initTenantList();
      },
      error: (error) => {
        let message = '';
        if (error.response.status === 409) {
          message = `テナント削除に失敗しました。\n契約期間内のデバイスが存在します。`;
        } else {
          message = `テナント削除に失敗しました。\nしばらくしてからもう一度お試しください。`;
        }
        const alertDialogOptions: AlertDialogOption = {
          type: 'error',
          message: message,
        };
        this.dialog.open(AlertDialogComponent, {
          width: '600px',
          data: alertDialogOptions,
        });
      },
    });
  }

  private _initTenantList() {
    this.tenantService.findAll().subscribe({
      next: (tenantList) => {
        this.tenantList = tenantList.map((tenant) => ({
          tenantId: tenant.tenantId,
          tenantName: tenant.tenantName,
          description: tenant.description,
          companyName: tenant.companyName,
        }));
      },
    });
  }
}
